import AddIcon from "@mui/icons-material/Add";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import _ from "lodash";
import React from "react";
import { useForm } from "react-hook-form";
import SearchInput from "../../components/SearchInput";
import { useVendorStore } from "../../services/vendors";
import { Vendor } from "../../types/vendors";
import { omitEmptyOptionalFields } from "../../utils/helper";
import { showError } from "../../services/toaster";
import {
  DEFAULT_CONFIRMATION_RULE,
  EscalationForm,
  WEEKDAYS,
  emptyEscalationForm,
  escalationFormFrom,
  toEscalationPayload,
} from "./escalation";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const defaultValue = {
  name: "",
  phone: "",
  email: "",
  integration: "labstack" as const,
  description: "",
  termsAndConditions: "",
  company: [],
  external: true,
  isActive: true,
}

const TabPanel = ({ children, value, index }: { children: React.ReactNode; value: number; index: number }) => (
  <Box hidden={value !== index} sx={{ pt: 2 }}>
    {value === index ? children : null}
  </Box>
);

const AddVendorDialog = ({ isModalOpen, toggleModal, selectedId }: any) => {
  const { onCreate, detail, onUpdate } = useVendorStore();
  const [data, setData] = React.useState<Vendor>(defaultValue);
  const [tabValue, setTabValue] = React.useState(0);
  const [escalation, setEscalation] = React.useState<EscalationForm>(emptyEscalationForm);
  const [holiday, setHoliday] = React.useState("");

  const { register, handleSubmit, formState: { errors }, reset } = useForm<Vendor>();

  const handleChange = (key: any, value: any) => setData(prev => ({ ...prev, [key]: value }));
  const changeEscalation = (patch: Partial<EscalationForm>) => setEscalation(prev => ({ ...prev, ...patch }));
  const handleClickOpen = () => toggleModal(true);
  const handleClose = () => toggleModal(false);

  const onSubmit = async () => {
    const escalationPayload = toEscalationPayload(escalation);
    if ("error" in escalationPayload) {
      setTabValue(2);
      showError(escalationPayload.error);
      return;
    }
    try {

      let response: Vendor | null = null;
      const payload: any = omitEmptyOptionalFields(_.cloneDeep(data));

      delete payload.company;
      delete payload.external;
      delete payload.escalation;
      if (data?._id) {
        payload.termsAndConditions = data.termsAndConditions?.trim() || "";
        // Always sent on edit so clearing every field removes the settings.
        payload.escalation = escalationPayload.value;
      } else if (escalationPayload.value) {
        payload.escalation = escalationPayload.value;
      }

      if (data?._id) {
        response = await onUpdate(payload);
      } else {
        response = await onCreate(payload);
      }



      handleClose();
    } catch (error) {
      console.error(error);
    }
  };

  const fetchDetail = async (selectedId: string) => {
    try {
      const res = await detail(selectedId);
      const data = res?.data;
      reset(data);
      setData(data);
      setEscalation(escalationFormFrom(data?.escalation));
    } catch (error) {
      console.error(error);
    }
  };

  React.useEffect(() => {
    setData(defaultValue);
    setEscalation(emptyEscalationForm());
    setHoliday("");
    setTabValue(0);
    if (selectedId) {
      fetchDetail(selectedId);
    }
  }, [selectedId]);

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
        <SearchInput />
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleClickOpen}
        >
          Add Vendor
        </Button>
      </Stack>

      <Dialog
        open={isModalOpen}
        onClose={handleClose}
        TransitionComponent={Transition}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { minHeight: "70vh" } }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>Add Vendor</DialogTitle>
          <DialogContent dividers>
            <Tabs value={tabValue} onChange={(_, value) => setTabValue(value)} aria-label="vendor tabs">
              <Tab label="Vendor Details" />
              <Tab label="Terms and Conditions" />
              <Tab label="Escalation" />
            </Tabs>

            <TabPanel value={tabValue} index={0}>
              <Stack spacing={2}>
                <TextField
                  id="name"
                  label="Full Name"
                  type="name"
                  fullWidth
                  variant="outlined"
                  value={data.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                />

                <TextField
                  id="phone"
                  label="Phone"
                  fullWidth
                  variant="outlined"
                  value={data.phone || ""}
                  onChange={(e) => handleChange("phone", e.target.value)}
                />

                <TextField
                  id="email"
                  label="Email"
                  type="email"
                  fullWidth
                  variant="outlined"
                  value={data.email || ""}
                  onChange={(e) => handleChange("email", e.target.value)}
                />

                <TextField
                  select
                  id="integration"
                  label="Integration"
                  fullWidth
                  variant="outlined"
                  value={data.integration}
                  onChange={(e) => handleChange("integration", e.target.value)}
                >
                  <MenuItem value="labstack">Labstack</MenuItem>
                  <MenuItem value="healthians">Healthians</MenuItem>
                  <MenuItem value="betacura">Betacura</MenuItem>
                </TextField>

                <TextField
                  id="description"
                  label="Description"
                  fullWidth
                  multiline
                  minRows={3}
                  variant="outlined"
                  value={data.description || ""}
                  onChange={(e) => handleChange("description", e.target.value)}
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={data.isActive !== false}
                      onChange={(e) => handleChange("isActive", e.target.checked)}
                      name="isActive"
                    />
                  }
                  label="Active"
                />
              </Stack>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <TextField
                id="termsAndConditions"
                label="Terms and Conditions"
                fullWidth
                multiline
                minRows={14}
                variant="outlined"
                value={data.termsAndConditions || ""}
                onChange={(e) => handleChange("termsAndConditions", e.target.value)}
                helperText={data._id ? `/api/v1/public/vendors/${data._id}/terms` : "Save vendor to get public terms link"}
              />
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              <Stack spacing={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={data.escalationEmails !== false}
                      onChange={(e) => handleChange("escalationEmails", e.target.checked)}
                      name="escalationEmails"
                    />
                  }
                  label="Send escalation emails to this vendor (operations always get them)"
                />
                <Typography variant="body2" color="text.secondary">
                  These settings apply to every {data.integration} booking. Leave fields blank to use the defaults.
                </Typography>
                <Typography fontWeight={600}>Unconfirmed booking alert</Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="Alert after"
                    type="number"
                    inputProps={{ min: 1, step: 1 }}
                    value={escalation.alertAmount}
                    onChange={(e) => changeEscalation({ alertAmount: e.target.value })}
                    helperText={`Default: ${DEFAULT_CONFIRMATION_RULE[data.integration]}`}
                  />
                  <TextField select label="Unit" value={escalation.alertUnit} sx={{ minWidth: 120 }}
                    onChange={(e) => changeEscalation({ alertUnit: e.target.value as EscalationForm["alertUnit"] })}>
                    <MenuItem value="minutes">Minutes</MenuItem>
                    <MenuItem value="hours">Hours</MenuItem>
                  </TextField>
                  <TextField select label="Counted from" value={escalation.alertFrom} sx={{ minWidth: 240 }}
                    onChange={(e) => changeEscalation({ alertFrom: e.target.value as EscalationForm["alertFrom"] })}>
                    <MenuItem value="">Default</MenuItem>
                    <MenuItem value="order">After the order reaches the lab</MenuItem>
                    <MenuItem value="appointment">Before the appointment</MenuItem>
                  </TextField>
                </Stack>

                <Typography fontWeight={600}>Working hours (IST)</Typography>
                <Typography variant="body2" color="text.secondary">
                  Confirmation and report alerts that fall due outside these hours, or on a holiday, are held until the lab opens.
                  No days ticked means every day.
                </Typography>
                <Stack direction="row" flexWrap="wrap">
                  {WEEKDAYS.map((day, index) => (
                    <FormControlLabel
                      key={day}
                      label={day}
                      control={
                        <Checkbox
                          checked={escalation.workingDays.includes(index)}
                          onChange={(e) => changeEscalation({
                            workingDays: e.target.checked
                              ? [...escalation.workingDays, index]
                              : escalation.workingDays.filter((value) => value !== index),
                          })}
                        />
                      }
                    />
                  ))}
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField label="Opens at" type="time" InputLabelProps={{ shrink: true }} value={escalation.workingHoursStart}
                    onChange={(e) => changeEscalation({ workingHoursStart: e.target.value })} />
                  <TextField label="Closes at" type="time" InputLabelProps={{ shrink: true }} value={escalation.workingHoursEnd}
                    onChange={(e) => changeEscalation({ workingHoursEnd: e.target.value })} />
                </Stack>

                <Typography fontWeight={600}>Holidays</Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <TextField label="Date" type="date" InputLabelProps={{ shrink: true }} value={holiday}
                    onChange={(e) => setHoliday(e.target.value)} />
                  <Button
                    disabled={!holiday || escalation.holidays.includes(holiday)}
                    onClick={() => { changeEscalation({ holidays: [...escalation.holidays, holiday].sort() }); setHoliday(""); }}
                  >
                    Add holiday
                  </Button>
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {escalation.holidays.map((date) => (
                    <Chip key={date} label={date}
                      onDelete={() => changeEscalation({ holidays: escalation.holidays.filter((value) => value !== date) })} />
                  ))}
                </Stack>
              </Stack>
            </TabPanel>



          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained">Submit</Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

export default AddVendorDialog;
